/* eslint-disable @typescript-eslint/consistent-type-definitions */
import type { AxiosResponse } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

type Data<T> = T | null;
type CustomError = Error | null;
type UseStrapiApiOptions<P, T> = {
	autoFetch?: boolean;
	params?: P;
	onError?: (error: { code?: number; data?: any }) => void;
	onSuccess?: (data?: Data<T>) => void;
};

export interface UseApiCall<T> {
	call: Promise<AxiosResponse<T>>;
	controller: AbortController;
}

interface UseStrapiApiResult<T, P> {
	loading: boolean;
	data: Data<T>;
	error: CustomError;
	fetch: (param: P, localOptions?: Partial<UseStrapiApiOptions<P, T>>) => void;
	onError: (cb: (error: { code?: number; data?: any }) => void) => void;
	onSuccess: (cb: (data?: Data<T>) => void) => void;
}

export const useStrapiApi = <T, P>(
	apiCall: (param: P) => UseApiCall<T>,
	options?: UseStrapiApiOptions<P, T>,
): UseStrapiApiResult<T, P> => {
	const [loading, setLoading] = useState<boolean>(false);
	const [data, setData] = useState<Data<T>>(null);
	const [error, setError] = useState<CustomError>(null);

	const onErrorRef = useRef(options?.onError);
	const onSuccessRef = useRef(options?.onSuccess);

	const fetch = useCallback(
		(param: P, localOptions?: Partial<UseStrapiApiOptions<P, T>>) => {
			const { call, controller } = apiCall(param);
			setLoading(true);

			call.then((response) => {
				setData(response.data);
				setError(null);
				(localOptions?.onSuccess ?? onSuccessRef.current)?.(response.data);
			})
				.catch((err) => {
					setError(err);
					(localOptions?.onError ?? onErrorRef.current)?.({
						code: err?.response?.status,
						data: err?.response?.data,
					});
				})
				.finally(() => {
					setLoading(false);
				});

			return () => controller.abort();
		},
		[apiCall],
	);

	useEffect(() => {
		if (options?.autoFetch && options.params !== undefined) {
			console.log('[useStrapiApi] autoFetch ON. params =', options.params);
			fetch(options.params);
		} else {
			console.log('[useStrapiApi] autoFetch SKIPPED. autoFetch:', options?.autoFetch, 'params:', options?.params);
		}
	}, [fetch, options?.autoFetch, options?.params]);

	return {
		loading,
		error,
		data,
		fetch,
		onError: (cb) => cb,
		onSuccess: (cb) => cb,
	};
};
